/**
 * CockpitClassPage — public/auth'd archive view of all published
 * Super Cockpit Class lessons. The most recent lesson is pinned as
 * "This week's class" at the top; older lessons list below.
 *
 * Free tier sees titles + teasers; full body is paywalled on the
 * detail page. This page itself is public so it doubles as a light
 * SEO/marketing surface.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { GraduationCap, ArrowRight, Sparkles } from 'lucide-react';
import { DepartmentNav } from '../components/DepartmentNav';
import { API_URL } from '../config/api';

const formatWeekOf = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T12:00:00Z');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

export const CockpitClassPage = () => {
  const [state, setState] = useState({ loading: true, lessons: [], err: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/cockpit-class/lessons`);
        if (!alive) return;
        setState({ loading: false, lessons: r.data?.lessons || [], err: null });
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, lessons: [], err: 'fetch_failed' });
      }
    })();
    return () => { alive = false; };
  }, []);

  const [thisWeek, ...older] = state.lessons;

  return (
    <div data-testid="cockpit-class-page" className="min-h-screen bg-background">
      <DepartmentNav />
      <main className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Hero */}
        <div className="space-y-3 pt-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-600 font-semibold">
            <GraduationCap className="w-4 h-4" /> Super Cockpit Class
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
            One metric a week, the way a super reads it.
          </h1>
          <p className="text-base text-muted-foreground max-w-2xl">
            Every week we unpack one dial on the cockpit — what it is, how we compute it,
            the one call it triggers, and the trap most homeowners fall into. Your vote
            and free-text feedback shape what we build next.
          </p>
        </div>

        {/* Loading */}
        {state.loading && (
          <div className="space-y-4">
            <Skeleton className="h-48 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
          </div>
        )}

        {/* Error / empty */}
        {!state.loading && !state.lessons.length && (
          <Card data-testid="cockpit-class-empty">
            <CardContent className="py-12 text-center text-muted-foreground">
              <p>No classes published yet. First one drops next Monday.</p>
            </CardContent>
          </Card>
        )}

        {/* This week pinned */}
        {thisWeek && (
          <Card data-testid="cockpit-class-pinned" className="border-emerald-500/40 bg-gradient-to-br from-emerald-50 via-background to-background dark:from-emerald-950/30">
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <Badge className="bg-emerald-500 text-white">
                  <Sparkles className="w-3 h-3 mr-1" /> This week
                </Badge>
                {thisWeek.metric_tag && (
                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 dark:text-emerald-300">
                    {thisWeek.metric_tag}
                  </Badge>
                )}
                {thisWeek.week_of && (
                  <span className="text-xs text-muted-foreground">Week of {formatWeekOf(thisWeek.week_of)}</span>
                )}
              </div>
              <CardTitle className="text-2xl flex items-start gap-3">
                <span className="text-4xl leading-none">{thisWeek.hero_emoji}</span>
                <span>{thisWeek.title}</span>
              </CardTitle>
              <CardDescription className="text-base mt-2">{thisWeek.dek}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {thisWeek.teaser_md && (
                <p className="text-sm text-foreground/80 leading-relaxed">{thisWeek.teaser_md}</p>
              )}
              <Link to={`/cockpit/class/${thisWeek.slug}`}>
                <Button data-testid="cockpit-class-pinned-cta" className="bg-emerald-600 hover:bg-emerald-700">
                  Open this week's class <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Older archive */}
        {older.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm uppercase tracking-widest text-muted-foreground font-semibold">Past classes</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {older.map((lesson) => (
                <Link
                  key={lesson.slug}
                  to={`/cockpit/class/${lesson.slug}`}
                  data-testid={`cockpit-class-archive-${lesson.slug}`}
                  className="group"
                >
                  <Card className="h-full transition hover:border-emerald-500/50">
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-1">
                        {lesson.metric_tag && (
                          <Badge variant="outline" className="text-[10px]">{lesson.metric_tag}</Badge>
                        )}
                        {lesson.week_of && (
                          <span className="text-[11px] text-muted-foreground">{formatWeekOf(lesson.week_of)}</span>
                        )}
                      </div>
                      <CardTitle className="text-base flex items-start gap-2">
                        <span className="text-2xl leading-none">{lesson.hero_emoji}</span>
                        <span className="group-hover:text-emerald-600 transition">{lesson.title}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-3">{lesson.teaser_md || lesson.dek}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CockpitClassPage;
